import { ColumnsType } from "antd/es/table";
import { IPayoffQuotesGet } from "../../../../../../../types/PayoffQute";
import { useCallback, useMemo } from "react";
import dayjs from "../../../../../../../configs/dayjs";
import {
  DATE_FORMAT,
  QUOTE_RECEIVER,
} from "../../../../../../../lib/constants";
import { getFormattedCurrency } from "../../../../../../../lib/currencyFormatter";
import { EditOutlined, EllipsisOutlined } from "@ant-design/icons";
import { Dropdown, MenuProps, Row } from "antd";
import { useButtonHandlers } from "./useButtonHandlers";
import { TPrintData } from "../types";
import { usePermissions } from "../../../../../../../lib/hooks/usePermissions";

type TTableColumnsProps = {
  setId: React.Dispatch<React.SetStateAction<string | null>>;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setItemToPrint: React.Dispatch<React.SetStateAction<TPrintData>>;
  reloadData: () => void;
  openPayoff: (id: string) => void;
};

export const useGetTableColumns = ({
  setId,
  setIsOpen,
  setItemToPrint,
  reloadData,
  openPayoff,
}: TTableColumnsProps) => {
  const { hasAccess } = usePermissions();

  const onEditClick = useCallback(
    (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
      e.stopPropagation();
      openPayoff(e.currentTarget.id);
    },
    [openPayoff]
  );

  const { onDeleteBtnClick } = useButtonHandlers({
    setIsOpen,
    setId,
    reloadData,
    setItemToPrint,
  });

  const onDelete = useCallback(
    (e: React.MouseEvent<HTMLInputElement>): void => {
      e.stopPropagation();
      onDeleteBtnClick(e.currentTarget.id);
    },
    [onDeleteBtnClick]
  );

  const onPrint = useCallback(
    (e: React.MouseEvent<HTMLInputElement>, id: string, type: number): void => {
      e.stopPropagation();
      setItemToPrint({ id, type });
    },
    [setItemToPrint]
  );

  const columns: ColumnsType<IPayoffQuotesGet> = useMemo(
    () => [
      {
        title: "Payoff Date",
        dataIndex: "date",
        key: "date",
        width: "17%",
        render: (value: string) =>
          value ? dayjs(value).format(DATE_FORMAT) : "-",
      },
      {
        title: "Buyer Amount",
        dataIndex: "buyerAmount",
        key: "buyerAmount",
        width: "17%",
        align: "right",
        render: (value: number) => getFormattedCurrency(value),
      },
      {
        key: "emptiness",
        width: "17%",
      },
      {
        title: "Quote Receiver",
        dataIndex: "type",
        key: "type",
        width: "13%",
        render: (value: number) =>
          QUOTE_RECEIVER.find((item) => item.value === value)?.label,
      },
      {
        title: "Last Updated",
        dataIndex: "lastUpdated",
        key: "lastUpdated",
        width: "26%",
        align: "right",
        render: (value: string) =>
          value ? dayjs(value).format(DATE_FORMAT) : "-",
      },
      {
        key: "actions",
        width: "10%",
        render: (_: any, record: IPayoffQuotesGet) => {
          const { id, type } = record;

          const items: MenuProps["items"] = [];

          hasAccess("ReportGeneration", "PayoffQuoteReport") &&
            items.push({
              key: "1",
              label: (
                <div
                  onClick={(e: React.MouseEvent<HTMLInputElement>) =>
                    onPrint(e, id, type)
                  }
                >
                  Print
                </div>
              ),
            });

          hasAccess("Payoffs", "Delete") &&
            items.push({
              key: "2",
              label: (
                <div id={id} onClick={onDelete}>
                  Delete
                </div>
              ),
            });

          return (
            <Row className="ant-table-actions ml-1">
              {hasAccess("Payoffs", "Modify") && (
                <EditOutlined
                  className="ant-table-btn"
                  id={id}
                  onClick={onEditClick}
                />
              )}
              {items.length > 0 && (
                <Dropdown menu={{ items }} trigger={["click"]} className="mr-3">
                  <EllipsisOutlined
                    className="ant-table-btn"
                    onClick={(e) => e.stopPropagation()}
                  />
                </Dropdown>
              )}
            </Row>
          );
        },
      },
    ],
    [onEditClick, onDelete, onPrint, hasAccess]
  );

  return { columns };
};
